<script setup lang="ts">
import ProductCartButton from '@/features/ProductCartButton/index.vue';
import ProductDetailInfo from '@/pages/product-detail/ui/productDetailInfo.vue';
import ProductsTabs from '@/pages/product-detail/ui/productsTabs.vue';
import UIButton from '@/shared/ui/Button/index';
import ProductsDetailImages from '@/pages/product-detail/ui/productsDetailImages.vue';
import HeartIcon from '@/shared/ui/Icons/Heart.vue';
import ProductDetailImagesSlider from '@/pages/product-detail/ui/productDetailImagesSlider.vue';
import type { IProduct } from '@/widgets/productCard/model/types/product';
import { formatSum } from '@/shared/utils/formatSum';

const { product } = defineProps<{ product: IProduct }>();

const emit = defineEmits<{
  openModal: [index: number];
}>();
const { $productsService } = useNuxtApp();
const { t } = useI18n();

const isMobile = inject<Ref<boolean>>('isMobile') || ref(false);
const isFavorite: Ref<boolean> = ref(product?.is_favorite || false);

const buttonText = computed(() => (isMobile.value ? '' : t('toFavorites')));
const productPrice = computed(() =>
  formatSum(product?.price_with_discount || product?.price),
);

const toggleFavorite = async (): Promise<void> => {
  try {
    isFavorite.value = !isFavorite.value;
    await $productsService.toggleFavorite(product?.id);
  } catch (error) {
    console.error('Ошибка при добавлении в избранное:', error);
  }
};
</script>

<template>
  <div class="product-detail-card">
    <div v-if="isMobile" class="product-detail-card__item _images">
      <ProductDetailImagesSlider
        :images="product.images"
        :product="product"
        is-show-badges
        image-height="223px"
        is-show-pagination
        is-show-modal
        @open-modal="emit('openModal', $event)"
      />
    </div>
    <div class="product-detail-card__item _info">
      <div class="product-detail-card__info">
        <h1 class="product-detail-card__title">
          {{ product.name }}
        </h1>
        <div class="product-detail-card__price">{{ productPrice }} UZS</div>
        <div class="product-detail-card__content">
          <div class="product-detail-card__brand">
            <div class="product-detail-card__brand-title">{{ t('brand') }}</div>
            <div class="product-detail-card__brand-name">{{ product.brand.name }}</div>
          </div>
          <div class="product-detail-card__actions">
            <ProductCartButton :product="product" class="product-detail-card__action" />
            <UIButton
              :text="buttonText"
              light-orange
              :class="['product-detail-card__action _favorite', { _active: isFavorite }]"
              @click="toggleFavorite"
            >
              <template #icon-left>
                <span class="product-detail-card__icon-wrapper">
                  <HeartIcon />
                </span>
              </template>
            </UIButton>
          </div>
        </div>
      </div>
      <div v-if="product.description" class="product-detail-card__info">
        <ProductDetailInfo :description="product.description" />
      </div>
      <div class="product-detail-card__info">
        <ProductsTabs :product="product" />
      </div>
    </div>
    <div v-if="!isMobile" class="product-detail-card__item _images">
      <ProductsDetailImages
        :images="product.images"
        :product="product"
        @open-modal="emit('openModal', $event)"
      />
    </div>
  </div>
</template>

<style lang="less">
.product-detail-card {
  display: flex;
  gap: @size-x5;

  &__title {
    .h-offset-reset();

    font-size: @size-x8;
    font-weight: 600;
    line-height: 100%;
  }

  &__item {
    &._info {
      .flex-column(@size-x6);

      width: 100%;
      max-width: 820px;
    }

    &._images {
      position: sticky;
      top: @size-x2;
      height: 100%;
      width: 100%;
      max-width: 580px;
    }
  }

  &__info {
    .flex-column(@size-x6);

    &:not(:last-child) {
      padding-bottom: @size-x10;
    }
  }

  &__brand {
    .flex-column(@size-x2);
  }

  &__brand-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 130%;
    color: @color-silver-chalice;
  }

  &__brand-name {
    font-size: @size-x4;
    font-weight: 600;
    line-height: 100%;
  }

  &__content {
    .flex-column(@size-x10);
  }

  &__icon-wrapper {
    display: inline-flex;
  }

  &__actions {
    display: inline-flex;
    gap: @size-x4;
  }

  &__action {
    max-width: 172px;

    &._favorite {
      max-width: 195px;

      path {
        stroke: @color-tango;
        transition: stroke @A linear;
      }

      &:hover path {
        stroke: #fff;
      }

      &._active {
        background-color: rgba(@color-tango, 0.75);
        color: #fff;

        path {
          fill: #fff;
          stroke: #fff;
        }
      }
    }
  }

  &__price {
    font-size: @size-x6;
    font-weight: 700;
    line-height: 100%;
  }

  @media @1024 {
    gap: @size-x8;
    flex-direction: column;

    &__title {
      font-size: 18px;
    }

    &__action {
      height: 36px;
      max-width: 100%;

      &._favorite {
        max-width: 44px;
      }
    }

    &__item {
      &._images {
        position: static;
        max-width: 100%;
      }

      &._info {
        gap: @size-x8;
        max-width: 100%;
      }
    }

    &__info {
      gap: @size-x3;

      &:not(:last-child) {
        padding-bottom: 0;
        border-bottom: 0;
      }
    }

    &__price {
      font-size: @size-x4;
    }

    &__content {
      gap: @size-x6;
    }
  }
}
</style>
